// =============================================================================
// io/sync/openPositions.ts — what an import left you holding.
// -----------------------------------------------------------------------------
// The store behind #12. A fills parser computes the leftover position on every
// import and, until now, threw it away; this keeps it so the NEXT import can
// close it into a real cross-session trade instead of inventing a phantom on
// the opposite side.
//
// 🚨 CONSUMED ROWS ARE MARKED, NEVER DELETED. Undo has to work in both
// directions — undoing the import that CREATED a position must remove it, and
// undoing the one that CONSUMED it must bring it back. A delete makes the second
// case unreversible, and undo already exists and is used.
//
// ⚠ WRITTEN ON COMMIT, NEVER ON PREVIEW. The import preview parses the file to
// show what would happen; if that also consumed positions, cancelling a preview
// would quietly eat them.
// =============================================================================

import type { SupabaseClient } from '@supabase/supabase-js';
import type { PriorPosition } from '../parsers/carryIn';
import type { OpenPosition } from '../parsers/sierra';
import { reportFailure, reportFailureAs } from '../diagnostics/errorLog';

const TABLE = 'open_positions';

interface Row {
  id: string;
  account: string | null;
  contract: string | null;
  contract_full: string | null;
  side: string | null;
  open_qty: number | string | null;
  avg_entry: number | string | null;
  open_time: string | null;
  open_fee: number | string | null;
  fingerprint: string | null;
}

const num = (v: unknown): number => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Positions still open for this user.
 *
 * ⚠ RETURNS [] ON ANY FAILURE, and that is the safe direction rather than a
 * shrug. With no prior positions every parser behaves exactly as it does today:
 * the import still lands, the carried-in close is still reported as unmatched,
 * and nothing is fabricated. Throwing here would block an import over a feature
 * that is an improvement, not a precondition.
 */
export async function loadOpenPositions(
  sb: SupabaseClient,
  userId: string,
): Promise<PriorPosition[]> {
  // ⚠ THE try/catch IS LOAD-BEARING, not belt-and-braces. The first version
  // handled a Supabase *error response* and let a THROW through — and a throw is
  // what you get from a client that has not been configured, or a table that
  // does not exist yet because the migration has not been applied. That reached
  // the import's outer catch and failed the whole import with a raw error,
  // which is the opposite of the degrade-quietly promise above. Found by the
  // import tests going red, not by reasoning about it.
  let data: unknown;
  try {
    const res = await sb
      .from(TABLE)
      .select('id,account,contract,contract_full,side,open_qty,avg_entry,open_time,open_fee,fingerprint')
      .eq('user_id', userId)
      .is('closed_by_batch', null);
    if (res.error || !res.data) return [];
    data = res.data;
  } catch {
    return [];
  }

  return (data as Row[])
    .filter((r) => r.side === 'long' || r.side === 'short')
    .map((r) => ({
      account: r.account ?? '',
      contract: r.contract ?? '',
      contractFull: r.contract_full,
      side: r.side as 'long' | 'short',
      openQty: num(r.open_qty),
      avgEntry: num(r.avg_entry),
      openTime: r.open_time ?? '',
      openFee: num(r.open_fee),
      fingerprint: r.fingerprint ?? '',
    }))
    .filter((p) => p.openQty > 0 && p.fingerprint.length > 0);
}

export interface OpenPositionWriteResult {
  opened: number;
  closed: number;
  error: string | null;
}

/**
 * Record what this import changed about the open book.
 *
 * @param left        positions this file leaves open
 * @param consumed    fingerprints of prior positions this file closed
 * @param batchId     the import batch, so undo can reverse either direction
 *
 * ⚠ CLOSE FIRST, THEN OPEN. A file that closes a position and opens a new one on
 * the same instrument would otherwise collide on the (user, fingerprint) unique
 * key while the old row is still live.
 *
 * ⚠ NEVER THROWS. A failure here leaves trades already written and correct; the
 * cost is that the next import will not know about a position, which is exactly
 * today's behaviour. Reported so the caller can say so, not raised.
 */
export async function recordOpenPositions(
  sb: SupabaseClient,
  userId: string,
  left: readonly OpenPosition[],
  consumed: readonly string[],
  batchId: string,
): Promise<OpenPositionWriteResult> {
  try {
    return await write(sb, userId, left, consumed, batchId);
  } catch (e) {
    // Same reasoning as the loader: the trades are already written and correct.
    return { opened: 0, closed: 0, error: reportFailure('record open positions', e) };
  }
}

async function write(
  sb: SupabaseClient,
  userId: string,
  left: readonly OpenPosition[],
  consumed: readonly string[],
  batchId: string,
): Promise<OpenPositionWriteResult> {
  let closed = 0;
  let opened = 0;

  if (consumed.length) {
    const { error, count } = await sb
      .from(TABLE)
      .update({ closed_by_batch: batchId }, { count: 'exact' })
      .eq('user_id', userId)
      .is('closed_by_batch', null)
      .in('fingerprint', [...consumed]);
    // ⚠ `reportFailureAs`, not `reportFailure`, so the recorded text is pinned
    // to `.message` whatever shape the client hands back.
    //
    // 🔴 THE REASON THIS USED TO GIVE WAS FALSE, and it had spread to five
    // files: "a returned Supabase error is a plain shape rather than an Error,
    // so the generic funnel would record [object Object]". Checked against the
    // INSTALLED library on 2026-08-27 — `PostgrestError extends Error`
    // (postgrest-js/dist/index.d.cts:26) and so does `AuthError`
    // (auth-js/.../errors.js:22). `reportFailure` would have returned
    // `e.message` perfectly well.
    //
    // ▶ The belief came from OUR OWN TEST DOUBLES, which resolve bare
    // `{ message: '…' }` objects. Pinning is still right — it is correct under
    // both shapes, and the doubles are what the suite actually exercises — but
    // it is belt-and-braces, not a rescue from `[object Object]`.
    //
    // This branch is the LIKELY failure (a denied write, a missing table); the
    // catch above only fires on a throw, so the two never double-log.
    if (error) {
      return {
        opened: 0, closed: 0,
        error: reportFailureAs('record open positions', error, error.message),
      };
    }
    closed = count ?? consumed.length;
  }

  if (left.length) {
    const rows = left.map((p) => ({
      user_id: userId,
      account: p.account,
      contract: p.contract,
      contract_full: p.contractFull,
      side: p.side,
      open_qty: p.openQty,
      avg_entry: p.avgEntry,
      open_time: p.openTime,
      open_fee: p.openFee,
      fingerprint: p.fingerprint,
      opened_by_batch: batchId,
    }));
    // ⚠ ignoreDuplicates: re-importing the same file must be a no-op here, not
    // a second copy of the same position. The unique key is what decides.
    const { error } = await sb
      .from(TABLE)
      .upsert(rows, { onConflict: 'user_id,fingerprint', ignoreDuplicates: true });
    if (error) {
      return {
        opened: 0, closed,
        error: reportFailureAs('record open positions', error, error.message),
      };
    }
    opened = rows.length;
  }

  return { opened, closed, error: null };
}

/**
 * Reverse this batch's effect on the open book.
 *
 * 🚨 BOTH DIRECTIONS, and the second is the one a delete would have lost:
 * positions this batch OPENED go away, and positions it CONSUMED come back.
 *
 * ⚠ Called by the import-undo path AFTER the trades are gone. Order matters
 * only in that a half-undo leaving trades deleted and positions stale is better
 * than the reverse — a stale open position is visible and correctable; a trade
 * that cannot be re-derived is not.
 */
export async function undoOpenPositionsForBatch(
  sb: SupabaseClient,
  userId: string,
  batchId: string,
): Promise<OpenPositionWriteResult> {
  try {
    return await reverse(sb, userId, batchId);
  } catch (e) {
    return { opened: 0, closed: 0, error: reportFailure('undo open positions', e) };
  }
}

async function reverse(
  sb: SupabaseClient,
  userId: string,
  batchId: string,
): Promise<OpenPositionWriteResult> {
  const del = await sb
    .from(TABLE)
    .delete({ count: 'exact' })
    .eq('user_id', userId)
    .eq('opened_by_batch', batchId);
  if (del.error) {
    return {
      opened: 0, closed: 0,
      error: reportFailureAs('undo open positions', del.error, del.error.message),
    };
  }

  const rest = await sb
    .from(TABLE)
    .update({ closed_by_batch: null }, { count: 'exact' })
    .eq('user_id', userId)
    .eq('closed_by_batch', batchId);
  if (rest.error) {
    return {
      opened: 0, closed: 0,
      error: reportFailureAs('undo open positions', rest.error, rest.error.message),
    };
  }

  return { opened: del.count ?? 0, closed: rest.count ?? 0, error: null };
}
